import { useEffect, useState } from 'react';

/**
 * Injects the BrightSaaS template's compiled CSS + JS (served from
 * /public/marketing) only while a marketing page is mounted, and removes
 * them on unmount so the bundle never bleeds into the Chakra app.
 *
 * Returns `cssReady` — false until app.css/theme-s5.css have finished
 * loading. The caller keeps the marketing tree hidden until then to avoid a
 * flash of unstyled content (raw full-width images snapping into layout once
 * the ~1.1MB stylesheet applies).
 *
 * The template's JS gates some initializers behind DOMContentLoaded/load/
 * resize. Those events have already fired by the time we inject the script,
 * so after it loads we re-dispatch them to trigger the gated initializers
 * (menubar height, marquee, etc.). Initializers that run on script parse
 * (menu toggle, dropdowns, carousels) just need the DOM to already exist —
 * which it does, because React has rendered the page by now.
 *
 * The compiled app.js is NOT idempotent: it appends canvases to <body> and
 * registers global listeners on first execution. Removing the <script> tag
 * on unmount does not undo those side effects, so re-injecting it on a later
 * SPA remount (e.g. after sign-out) leaves a broken/blank page. So once the
 * bundle has fully executed in this document, any subsequent mount of the
 * marketing tree forces a single clean reload — it then boots from a fresh
 * document exactly like the initial (working) load.
 *
 * `bundleExecuted` is set in script.onload (not at mount) and is never reset,
 * so React 18 StrictMode's mount→unmount→mount cycle in dev does not trip the
 * reload: the StrictMode unmount happens before the async script finishes, so
 * the flag is still false on the immediate second mount.
 */

const BASE = '/marketing';
const STYLES = [`${BASE}/css/app.css`, `${BASE}/css/theme-s5.css`];
const SCRIPT = `${BASE}/js/app.js`;

let bundleExecuted = false;

export function useBrightSaaSAssets() {
  const [cssReady, setCssReady] = useState(false);

  useEffect(() => {
    if (bundleExecuted) {
      window.location.reload();
      return;
    }

    document.body.classList.add('nk-body');

    // Each effect run owns its readiness. React 18 StrictMode runs the effect
    // twice in dev (run → cleanup → run); a torn-down run's in-flight
    // stylesheet load events must NOT mark the page ready, or the cover is
    // removed before the live app.css has applied (unstyled light flash).
    let cancelled = false;
    let loadedCount = 0;
    const onStyleLoad = () => {
      if (cancelled) return;
      loadedCount += 1;
      if (loadedCount >= STYLES.length) {
        // A <link>'s load event can fire a beat before the browser has
        // recalculated/applied the stylesheet for the next paint. Reveal
        // after two animation frames so styles are applied (under the
        // cover) before it is removed — otherwise one unstyled frame shows.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (!cancelled) setCssReady(true);
          });
        });
      }
    };

    const links = STYLES.map((href) => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      link.dataset.brightsaas = 'true';
      link.addEventListener('load', onStyleLoad);
      link.addEventListener('error', onStyleLoad);
      document.head.appendChild(link);
      return link;
    });

    const script = document.createElement('script');
    script.src = SCRIPT;
    script.async = false;
    script.dataset.brightsaas = 'true';
    script.onload = () => {
      bundleExecuted = true;
      // Re-fire the lifecycle events the template's initializers listen for.
      window.dispatchEvent(new Event('DOMContentLoaded'));
      window.dispatchEvent(new Event('load'));
      window.dispatchEvent(new Event('resize'));
      // Signal React-side code that the Nio bundle is now available.
      window.dispatchEvent(new Event('brightsaas:loaded'));
    };
    document.body.appendChild(script);

    return () => {
      cancelled = true;
      document.body.classList.remove('nk-body');
      links.forEach((l) => {
        l.removeEventListener('load', onStyleLoad);
        l.removeEventListener('error', onStyleLoad);
        l.remove();
      });
      script.remove();
    };
  }, []);

  return cssReady;
}
